(function () {
  'use strict';

  class DynamicSavingsBadge {
    constructor() {
      this.productForm = document.querySelector('product-form');
      this.savingsBadgeElement = document.querySelector('[data-ref="productSavingsBadge"]');
      this.variantIdInput = document.querySelector('[data-ref="variantIdInput"]');

      console.log('[DynamicSavingsBadge] Initializing...', {
        productForm: !!this.productForm,
        badgeElement: !!this.savingsBadgeElement,
        variantInput: !!this.variantIdInput,
      });

      // Don't initialize if badge element doesn't exist
      if (!this.savingsBadgeElement) {
        console.warn('[DynamicSavingsBadge] Badge element not found, skipping initialization');
        return;
      }

      // Check if metafield badge exists (it takes precedence)
      const metafieldBadge = document.querySelector('.product-savings-badge--metafield');
      if (metafieldBadge && metafieldBadge.offsetParent !== null) {
        // Metafield badge is visible, hide dynamic badge
        console.log('[DynamicSavingsBadge] Metafield badge is visible, hiding dynamic badge');
        this.savingsBadgeElement.style.display = 'none';
        return;
      }

      // Get product data from the product-form element
      this.productData = this.getProductData();
      this.currentVariantId = this.getCurrentVariantId();

      console.log('[DynamicSavingsBadge] Product data loaded', {
        hasProductData: !!this.productData,
        variantsCount: this.productData?.variants?.length,
        currentVariantId: this.currentVariantId,
      });

      // Get metafield colors for styling
      this.badgeBackgroundColor = this.savingsBadgeElement.style.backgroundColor || '';
      this.badgeTextColor = this.savingsBadgeElement.style.color || '';

      this.init();
    }

    init() {
      // Initial calculation
      this.updateBadge();

      // Monitor URL changes for variant parameter
      this.startMonitoring();

      // Listen for variant changes
      this.listenForVariantChanges();

      // Listen for Recharge widget events
      this.setupRechargeListener();
    }

    getProductData() {
      if (!this.productForm) {
        return null;
      }

      try {
        const productAttr = this.productForm.getAttribute('product');
        if (productAttr) {
          return JSON.parse(productAttr);
        }
      } catch (e) {
        console.error('Failed to parse product data:', e);
      }
      return null;
    }

    getCurrentVariantId() {
      if (this.variantIdInput) {
        return parseInt(this.variantIdInput.value);
      }

      // Fallback to URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const variantParam = urlParams.get('variant');
      if (variantParam) {
        return parseInt(variantParam);
      }

      // Fallback to default variant
      if (this.productData && this.productData.selected_or_first_available_variant) {
        return this.productData.selected_or_first_available_variant.id;
      }

      return null;
    }

    getCurrentVariant() {
      if (!this.productData || !this.productData.variants) {
        return null;
      }

      const variantId = this.getCurrentVariantId();
      return this.productData.variants.find((v) => v.id === variantId) || null;
    }

    calculateSavingsPercentage(price, compareAtPrice) {
      if (!compareAtPrice || compareAtPrice <= 0 || price >= compareAtPrice) {
        return 0;
      }

      const savingsAmount = compareAtPrice - price;
      const percentage = (savingsAmount / compareAtPrice) * 100;
      return Math.round(percentage);
    }

    hasSellingPlan() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.has('selling_plan') && urlParams.get('selling_plan') !== '';
    }

    parsePriceFromText(priceText) {
      // Remove currency symbols and parse price
      const cleaned = priceText.replace(/[^0-9.]/g, '');
      const dollars = parseFloat(cleaned);
      return dollars ? Math.round(dollars * 100) : null; // Convert to cents
    }

    updateBadge() {
      const currentVariant = this.getCurrentVariant();

      if (!currentVariant || !this.savingsBadgeElement) {
        console.log('[DynamicSavingsBadge] updateBadge: No variant or badge element', {
          hasVariant: !!currentVariant,
          hasBadgeElement: !!this.savingsBadgeElement,
        });
        return;
      }

      // Check if metafield badge is visible (it takes precedence)
      const metafieldBadge = document.querySelector('.product-savings-badge--metafield');
      if (metafieldBadge && metafieldBadge.offsetParent !== null) {
        console.log('[DynamicSavingsBadge] Metafield badge visible, hiding dynamic badge');
        this.savingsBadgeElement.style.display = 'none';
        return;
      }

      // Hide badge when one-time purchase is selected (no subscription)
      const hasSubscription = this.hasSellingPlan();
      if (!hasSubscription) {
        console.log('[DynamicSavingsBadge] One-time purchase selected, hiding badge');
        this.savingsBadgeElement.style.display = 'none';
        return;
      }

      let price, compareAtPrice;

      if (hasSubscription) {
        // When subscription is active:
        // - Get current subscription price from displayed price element (discounted price)
        // - Use variant's base price (one-time price) as compare_at_price
        const priceElement = document.querySelector('[data-ref="productPrice"]');

        if (priceElement) {
          // Parse displayed subscription price
          const subscriptionPriceText = priceElement.textContent.trim();
          price = this.parsePriceFromText(subscriptionPriceText);

          // Use variant's base price as compare_at_price (one-time price)
          compareAtPrice = currentVariant.price;

          console.log('[DynamicSavingsBadge] Subscription prices:', {
            subscriptionPriceText,
            price,
            compareAtPrice,
            variantBasePrice: currentVariant.price,
          });
        } else {
          // Fallback: use variant price as base, but this won't show discount
          price = currentVariant.price;
          compareAtPrice = currentVariant.price; // No discount to show
        }
      } else {
        // When no subscription: use variant's compare_at_price if it exists
        price = currentVariant.price;
        compareAtPrice = currentVariant.compare_at_price;
      }

      console.log('[DynamicSavingsBadge] updateBadge:', {
        variantId: currentVariant.id,
        price,
        compareAtPrice,
        hasCompareAtPrice: !!compareAtPrice,
        hasSubscription,
      });

      // Hide badge if no compare_at_price exists or prices are invalid
      if (!compareAtPrice || compareAtPrice <= 0 || !price || price <= 0) {
        console.log('[DynamicSavingsBadge] No valid compare_at_price or price, hiding badge');
        this.savingsBadgeElement.style.display = 'none';
        return;
      }

      // Calculate savings percentage
      const savingsPercentage = this.calculateSavingsPercentage(price, compareAtPrice);

      console.log('[DynamicSavingsBadge] Calculated savings:', savingsPercentage + '%');

      // Update data attributes
      this.savingsBadgeElement.setAttribute('data-variant-price', price);
      this.savingsBadgeElement.setAttribute('data-variant-compare-at-price', compareAtPrice || '');

      // Show/hide badge and update text
      if (savingsPercentage > 0 && compareAtPrice && compareAtPrice > price) {
        // Show badge with calculated percentage
        this.savingsBadgeElement.textContent = `Save ${savingsPercentage}%`;
        this.savingsBadgeElement.style.display = '';
        console.log('[DynamicSavingsBadge] Badge shown:', `Save ${savingsPercentage}%`);

        // Apply metafield colors if they exist
        if (this.badgeBackgroundColor) {
          this.savingsBadgeElement.style.backgroundColor = this.badgeBackgroundColor;
        }
        if (this.badgeTextColor) {
          this.savingsBadgeElement.style.color = this.badgeTextColor;
        }
      } else {
        // Hide badge when no savings
        this.savingsBadgeElement.style.display = 'none';
        console.log('[DynamicSavingsBadge] Badge hidden - no savings');
      }
    }

    startMonitoring() {
      // Check URL every 250ms (same as ProductForm component)
      this.monitorInterval = setInterval(() => {
        this.updateBadge();
      }, 250);
    }

    listenForVariantChanges() {
      if (this.variantIdInput) {
        this.variantIdInput.addEventListener('change', () => {
          console.log('[DynamicSavingsBadge] Variant input changed');
          this.currentVariantId = this.getCurrentVariantId();
          this.updateBadge();
        });
      } else {
        console.warn('[DynamicSavingsBadge] Variant input not found');
      }

      // Also listen for custom events that might be dispatched
      document.addEventListener('variant:changed', () => {
        console.log('[DynamicSavingsBadge] variant:changed event received');
        this.currentVariantId = this.getCurrentVariantId();
        this.updateBadge();
      });
    }

    setupRechargeListener() {
      // Listen for Recharge widget ready event
      const originalRechargeReady = window.RechargeSubscriptionWidgetReady;

      window.RechargeSubscriptionWidgetReady = (api) => {
        // Call original handler if it exists
        if (typeof originalRechargeReady === 'function') {
          originalRechargeReady(api);
        }

        // Add our own listener
        if (api && api.events) {
          api.events.onVariantChange((variantId) => {
            console.log('[DynamicSavingsBadge] Recharge variant changed:', variantId);
            this.currentVariantId = variantId;
            // Small delay to ensure URL is updated
            setTimeout(() => {
              this.updateBadge();
            }, 50);
          });

          api.events.onSellingPlanChange((planId) => {
            console.log('[DynamicSavingsBadge] Recharge selling plan changed:', planId);
            // Small delay to ensure URL is updated
            setTimeout(() => {
              this.updateBadge();
            }, 50);
          });
        }
      };
    }

    destroy() {
      if (this.monitorInterval) {
        clearInterval(this.monitorInterval);
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new DynamicSavingsBadge();
    });
  } else {
    new DynamicSavingsBadge();
  }
})();
